import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Clock, Search, ArrowRight, Building2, Package, X } from "lucide-react";
import useSeo from "../hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom pin icon for pickup points
const pickupIcon = (active = false) =>
  L.divIcon({
    className: "ema-pickup-marker",
    html: `
      <div style="position:relative;width:40px;height:50px;transform:translate(-50%,-100%);">
        <div style="position:absolute;left:6px;top:0;width:28px;height:28px;border-radius:50%;
          background:${active ? "#DC2626" : "#0A1128"};border:3px solid white;
          box-shadow:0 6px 14px rgba(10,17,40,.35);
          display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:12px;
          font-family:'Outfit',sans-serif;">●</div>
        <div style="position:absolute;left:50%;bottom:0;transform:translateX(-50%);width:0;height:0;
          border-left:6px solid transparent;border-right:6px solid transparent;
          border-top:10px solid ${active ? "#DC2626" : "#0A1128"};"></div>
      </div>`,
    iconSize: [40, 50],
    iconAnchor: [0, 0],
    popupAnchor: [0, -40],
  });

const isOpenNow = (hoursStr) => {
  // Naive heuristic: "Man-Fre 09-18, Lør 10-15" — extract first range, compare to current hour
  if (!hoursStr) return null;
  const m = hoursStr.match(/(\d{1,2})\s*-\s*(\d{1,2})/);
  if (!m) return null;
  const start = parseInt(m[1], 10);
  const end = parseInt(m[2], 10);
  const now = new Date();
  const day = now.getDay(); // 0 Sun..6 Sat
  // Sunday closed unless 'søn' in string
  const lower = hoursStr.toLowerCase();
  if (day === 0 && !lower.includes("søn")) return false;
  return now.getHours() >= start && now.getHours() < end;
};

const StatusPill = ({ open }) => {
  if (open === null) return null;
  return open ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" /> Åben nu
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">
      Lukket
    </span>
  );
};

function PickupCard({ p, onSelect, active }) {
  const open = isOpenNow(p.opening_hours);
  return (
    <div
      className={`bg-white rounded-xl p-5 border cursor-pointer transition-all ${
        active ? "border-brand-red shadow-md" : "border-[#E2E8F0] hover:border-[#0A1128] hover:shadow-sm"
      }`}
      onClick={() => onSelect(p)}
      data-testid={`pickup-card-${p.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-[#0A1128] text-base truncate">{p.shop_name}</div>
          <div className="text-sm text-[#475569] mt-0.5 flex items-center gap-1.5">
            <MapPin size={11} className="shrink-0" />
            <span className="truncate">{p.shop_address}, {p.shop_zip} {p.shop_city}</span>
          </div>
          <div className="text-xs text-[#475569] mt-1.5 flex items-center gap-1.5">
            <Clock size={11} /> {p.opening_hours}
          </div>
          {typeof p.distance_km === "number" && (
            <div className="text-xs text-brand-red mt-1 font-semibold">{p.distance_km} km væk</div>
          )}
        </div>
        <StatusPill open={open} />
      </div>
      {p.business_type && (
        <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#475569] bg-[#F1F5F9] px-2 py-0.5 rounded">
          <Building2 size={10} /> {p.business_type}
        </div>
      )}
    </div>
  );
}

export default function FindPickupPointPage() {
  useSeo({
    title: "Find EMA Pickup Point · Pakke-afhentning på Sjælland · EMA Kurer ApS",
    description: "Find din nærmeste EMA Pickup Point på Sjælland. Hent dine pakker trygt og fleksibelt hos lokale partner-butikker.",
    path: "/find-pickup-point",
  });

  const [params] = useSearchParams();
  const preselectId = params.get("id");

  const [points, setPoints] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState(null);
  const [sheetOpen, setSheetOpen] = React.useState(false);

  const [userCoords, setUserCoords] = React.useState(null);

  React.useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  }, []);

  React.useEffect(() => {
    const loadPoints = async () => {
      setLoading(true);
      try {
        if (userCoords) {
          const { data } = await axios.get(`${API}/pakkeshops/near-geo`, {
            params: { lat: userCoords.lat, lng: userCoords.lng, radius_km: 50 },
          });
          const list = (data.pakkeshops || []).filter((p) => p.shop_coords);
          setPoints(list);
          if (preselectId) {
            const match = list.find((p) => p.id === preselectId);
            if (match) {
              setSelected(match);
              setSheetOpen(true);
            }
          }
          return;
        }
        const { data } = await axios.get(`${API}/pickup-points/public`);
        const list = (data.pickup_points || []).filter((p) => p.shop_coords);
        setPoints(list);
        if (preselectId) {
          const match = list.find((p) => p.id === preselectId);
          if (match) {
            setSelected(match);
            setSheetOpen(true);
          }
        }
      } catch {
        setPoints([]);
      } finally {
        setLoading(false);
      }
    };
    loadPoints();
  }, [preselectId, userCoords]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return points;
    return points.filter(
      (p) =>
        (p.shop_name || "").toLowerCase().includes(q) ||
        (p.shop_zip || "").includes(q) ||
        (p.shop_city || "").toLowerCase().includes(q) ||
        (p.shop_address || "").toLowerCase().includes(q),
    );
  }, [points, query]);

  // Default map center = Copenhagen
  const center = selected?.shop_coords
    ? [selected.shop_coords.lat, selected.shop_coords.lng]
    : [55.6761, 12.5683];
  const zoom = selected ? 14 : 10;

  return (
    <div className="bg-[#F1F5F9] min-h-screen" data-testid="find-pickup-point-page">
      {/* HERO */}
      <section className="bg-[#0A1128] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 lg:py-14">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-red border border-brand-red/40 rounded-full px-3 py-1.5">
            <Package size={12} /> EMA Pickup Point
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-4 leading-[1.05]">
            Find din nærmeste pickup point.
          </h1>
          <p className="text-white/75 mt-3 max-w-2xl">
            Hent dine pakker trygt hos lokale partnere på Sjælland — også uden for posthusets åbningstid.
          </p>
          {/* Search */}
          <div className="mt-6 max-w-xl">
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#475569]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Søg på postnummer, by eller butiksnavn..."
                className="w-full bg-white text-[#0A1128] placeholder-[#94a3b8] pl-12 pr-4 py-3.5 rounded-lg shadow-md border border-white/10 focus:outline-none focus:ring-2 focus:ring-brand-red"
                data-testid="pickup-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* MAP + LIST */}
      <div className="max-w-7xl mx-auto px-0 lg:px-8 py-0 lg:py-8 grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-0 lg:gap-6">
        {/* Sidebar list — hidden on mobile by default, shown in bottom-sheet */}
        <aside className="hidden lg:block bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
          <div className="p-5 border-b border-[#E2E8F0]">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">
              {loading ? "Indlæser..." : `${filtered.length} pickup points`}
            </div>
          </div>
          <div className="max-h-[600px] overflow-y-auto p-4 space-y-3" data-testid="pickup-list-desktop">
            {loading && <div className="text-sm text-[#475569] text-center py-8">Indlæser pickup points...</div>}
            {!loading && filtered.length === 0 && (
              <div className="text-sm text-[#475569] text-center py-8">Ingen pickup points fundet.</div>
            )}
            {filtered.map((p) => (
              <PickupCard key={p.id} p={p} onSelect={(x) => { setSelected(x); setSheetOpen(true); }} active={selected?.id === p.id} />
            ))}
          </div>
        </aside>

        {/* Map */}
        <div className="relative h-[60vh] lg:h-[640px] lg:rounded-xl overflow-hidden lg:border lg:border-[#E2E8F0] bg-white">
          <MapContainer key={`${center[0]}-${center[1]}-${zoom}`} center={center} zoom={zoom} className="w-full h-full">
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            {filtered.map((p) => (
              <Marker
                key={p.id}
                position={[p.shop_coords.lat, p.shop_coords.lng]}
                icon={pickupIcon(selected?.id === p.id)}
                eventHandlers={{ click: () => { setSelected(p); setSheetOpen(true); } }}
              >
                <Popup>
                  <strong>{p.shop_name}</strong><br />
                  {p.shop_address}, {p.shop_zip} {p.shop_city}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Mobile bottom-sheet for list */}
        <div className="lg:hidden">
          <div className={`fixed inset-x-0 bottom-0 z-40 bg-white rounded-t-3xl shadow-[0_-8px_32px_rgba(0,0,0,0.12)] border-t border-[#E2E8F0] transition-transform duration-300 ${sheetOpen ? "translate-y-0" : "translate-y-[calc(100%-72px)]"}`} data-testid="pickup-bottom-sheet">
            <div
              className="py-3 cursor-pointer"
              onClick={() => setSheetOpen((s) => !s)}
              data-testid="pickup-bottom-sheet-handle"
            >
              <div className="mx-auto w-12 h-1.5 bg-[#E2E8F0] rounded-full" />
              <div className="text-center text-xs font-bold uppercase tracking-[0.2em] text-[#475569] mt-2">
                {loading ? "Indlæser..." : `${filtered.length} pickup points`}
              </div>
            </div>
            <div className="max-h-[55vh] overflow-y-auto px-4 pb-8 space-y-3" data-testid="pickup-list-mobile">
              {filtered.map((p) => (
                <PickupCard key={p.id} p={p} onSelect={(x) => setSelected(x)} active={selected?.id === p.id} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Detail drawer (right side on desktop, modal on mobile) */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/50 lg:bg-transparent flex items-end lg:items-center lg:justify-end p-0 lg:p-8"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white w-full lg:w-[420px] lg:rounded-xl rounded-t-3xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="pickup-detail-drawer"
          >
            <div className="bg-[#0A1128] text-white p-5 relative">
              <button onClick={() => setSelected(null)} className="absolute top-3 right-3 text-white/60 hover:text-white" data-testid="pickup-detail-close">
                <X size={20} />
              </button>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">EMA Pickup Point</div>
              <h2 className="font-display font-bold text-2xl mt-2 tracking-tight" data-testid="pickup-detail-name">{selected.shop_name}</h2>
              <div className="text-sm text-white/75 mt-1.5 flex items-center gap-1.5"><MapPin size={12} /> {selected.shop_address}, {selected.shop_zip} {selected.shop_city}</div>
              <div className="mt-3"><StatusPill open={isOpenNow(selected.opening_hours)} /></div>
            </div>
            <div className="p-6 space-y-4">
              {selected.shop_photos?.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {selected.shop_photos.slice(0, 3).map((ph, i) => (
                    <div key={i} className="aspect-square bg-[#F1F5F9] rounded-lg overflow-hidden">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${ph.url}`} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">Åbningstider</div>
                <div className="text-sm text-[#0A1128] mt-1">{selected.opening_hours}</div>
              </div>
              {selected.business_type && (
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">Type forretning</div>
                  <div className="text-sm text-[#0A1128] mt-1 capitalize">{selected.business_type}</div>
                </div>
              )}
              <Link
                to={`/book?delivery_to_pickup=${selected.id}`}
                className="btn-primary w-full justify-center mt-2"
                data-testid="pickup-detail-book"
              >
                Send pakke til denne pickup point <ArrowRight size={16} />
              </Link>
              <p className="text-xs text-[#475569] text-center">
                Du kan vælge denne pickup point som leveringsadresse i booking-flowet.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
